import { API_BASE_URL, API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  FiberIncomingListResponse,
  FiberLinkDetailsResponse,
  FiberNodeDetailsResponse,
  FiberTracingResponse,
  IncomingTransactionDetail,
  ReceiveIncomingPayload,
} from './types';

export async function fetchIncomingTransactions(
  qs: string,
): Promise<FiberIncomingListResponse> {
  return apiFetch<FiberIncomingListResponse>(
    `/v3/fiber/transactions/incomings?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchOutgoingTransactions(
  qs: string,
): Promise<import('./types').FiberOutgoingListResponse> {
  return apiFetch<import('./types').FiberOutgoingListResponse>(
    `/v3/fiber/transactions/outgoings?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchAllCompaniesForImport(): Promise<{
  data: Array<{
    id: string;
    name: string;
    officialName: string;
    connectionType?: string | null;
    country?: string | null;
  }>;
}> {
  return apiFetch<{
    data: Array<{
      id: string;
      name: string;
      officialName: string;
      connectionType?: string | null;
      country?: string | null;
    }>;
  }>('/api/companies?isDropDown=true&includeOwnCompany=true', {}, API_ROOT_URL);
}

export async function fetchFiberLotsForImport(qs: string): Promise<{
  data: Array<{
    id: string;
    name: string;
    quantity: number;
    unit: string;
    productId: string;
    productionFacilityProcesses?: string[];
  }>;
}> {
  return apiFetch<{
    data: Array<{
      id: string;
      name: string;
      quantity: number;
      unit: string;
      productId: string;
      productionFacilityProcesses?: string[];
    }>;
  }>(`/v3/fiber/lots?${qs}`, {}, API_BASE_URL);
}

export async function createFiberBulkLots(
  lots: Array<{
    name: string;
    storage?: string;
    productId: string;
    index: number;
  }>,
): Promise<{ data: Array<{ id: string; index: number }> }> {
  return apiFetch<{ data: Array<{ id: string; index: number }> }>(
    '/v3/fiber/lots/bulk-create',
    { method: 'POST', body: JSON.stringify(lots) },
    API_BASE_URL,
  );
}

export async function fetchAttachmentTemplatesFiber(): Promise<{
  data: Array<{ id: string; name: string; tracingType?: string }>;
}> {
  return apiFetch<{
    data: Array<{ id: string; name: string; tracingType?: string }>;
  }>('/v3/attachment-templates?isDropDown=true', {}, API_BASE_URL);
}

export async function createFiberTransaction(
  body: Record<string, unknown>,
): Promise<{ data: unknown }> {
  return apiFetch<{ data: unknown }>(
    '/v3/fiber/transactions',
    { method: 'POST', body: JSON.stringify(body) },
    API_BASE_URL,
  );
}

export async function fetchFiberProductsForCompany(companyId: string): Promise<{
  data: Array<{
    id: string;
    name: string;
    code: string;
    stockUnit?: string | null;
    productCategoryQuantityUnits?: string[];
  }>;
}> {
  return apiFetch<{
    data: Array<{
      id: string;
      name: string;
      code: string;
      stockUnit?: string | null;
      productCategoryQuantityUnits?: string[];
    }>;
  }>(
    `/v3/companies/${companyId}/created-network-products?isDropDown=true`,
    {},
    API_BASE_URL,
  );
}

export async function fiberStocksAvailableProducts(
  qs: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v3/fiber/stocks/available-products?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchFiberSettings(): Promise<
  Array<{ key: string; value: string[] }>
> {
  return apiFetch<Array<{ key: string; value: string[] }>>(
    '/v3/fiber/settings',
    {},
    API_BASE_URL,
  );
}

export async function fetchFiberLotsByProductId(qs: string): Promise<{
  data: Array<{
    id: string;
    name: string;
    quantity: number;
    unit: string;
    productId: string;
    productionFacilityProcesses?: string[];
  }>;
}> {
  return apiFetch<{
    data: Array<{
      id: string;
      name: string;
      quantity: number;
      unit: string;
      productId: string;
      productionFacilityProcesses?: string[];
    }>;
  }>(`/v3/fiber/lots?${qs}`, {}, API_BASE_URL);
}

export async function createFiberLot(payload: {
  name: string;
  storage?: string;
  productId: string;
  isPublic?: boolean;
}): Promise<{ data: { id: string; name: string } }> {
  return apiFetch<{ data: { id: string; name: string } }>(
    '/v3/fiber/lots',
    {
      method: 'POST',
      body: JSON.stringify({ ...payload, isPublic: payload.isPublic ?? true }),
    },
    API_BASE_URL,
  );
}

export async function createFiberCompany(payload: {
  officialName: string;
  facilityProcesses: string;
  country: string;
  connectionType: string;
}): Promise<{
  data: {
    id: string;
    officialName: string;
    country: string;
    facilityProcesses: string;
  };
}> {
  return apiFetch<{
    data: {
      id: string;
      officialName: string;
      country: string;
      facilityProcesses: string;
    };
  }>(
    '/api/companies',
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function fetchAttachmentTemplateRules(
  templateId: string,
  qs: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/api/attachment-templates/${templateId}/documents?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function attachFiberTransactionDocuments(
  fiberTransactionId: string,
  documents: Array<{ fileId: string; type: string; name: string }>,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/v3/fiber/transactions/attach-documents',
    { method: 'POST', body: JSON.stringify({ fiberTransactionId, documents }) },
    API_BASE_URL,
  );
}

export async function removeFiberTransactionDocument(
  fiberTransactionId: string,
  documentId: string,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/v3/fiber/transactions/remove-document',
    {
      method: 'POST',
      body: JSON.stringify({ fiberTransactionId, documentId }),
    },
    API_BASE_URL,
  );
}

export async function fetchOutgoingTransactionById(
  txId: string,
): Promise<{ data: unknown }> {
  return apiFetch<{ data: unknown }>(
    `/v3/fiber/transactions/outgoings/${txId}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchIncomingTransactionById(
  txId: string,
): Promise<{ data: IncomingTransactionDetail } | IncomingTransactionDetail> {
  return apiFetch<
    { data: IncomingTransactionDetail } | IncomingTransactionDetail
  >(`/v3/fiber/transactions/incomings/${txId}`, {}, API_BASE_URL);
}

export async function fetchIncomingTransactionTracing(
  txId: string,
): Promise<FiberTracingResponse> {
  return apiFetch<FiberTracingResponse>(
    `/v3/fiber/transactions/incoming/${txId}/tracing`,
    {},
    API_BASE_URL,
  );
}

export async function receiveIncomingTransaction(
  payload: ReceiveIncomingPayload,
): Promise<{ data: { id: string } }> {
  return apiFetch<{ data: { id: string } }>(
    '/v3/fiber/transactions',
    { method: 'POST', body: JSON.stringify(payload) },
    API_BASE_URL,
  );
}

export async function fetchOutgoingTransactionTracing(
  txId: string,
): Promise<FiberTracingResponse> {
  return apiFetch<FiberTracingResponse>(
    `/v3/fiber/transactions/outgoing/${txId}/tracing`,
    {},
    API_BASE_URL,
  );
}

export async function fetchFiberLinkDetails(
  qs: string,
): Promise<FiberLinkDetailsResponse> {
  return apiFetch<FiberLinkDetailsResponse>(
    `/v3/fiber/transactions/transactions-by-link-ids?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchFiberNodeDetails(
  qs: string,
): Promise<FiberNodeDetailsResponse> {
  return apiFetch<FiberNodeDetailsResponse>(
    `/v3/fiber/transactions/nodes?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function createNetworkProduct(payload: {
  name: string;
  code: string;
  companyId: string;
}): Promise<{ data: { id: string; name: string; code: string } }> {
  return apiFetch<{ data: { id: string; name: string; code: string } }>(
    '/v3/products/create-with-components',
    { method: 'POST', body: JSON.stringify(payload) },
    API_BASE_URL,
  );
}
